import { axiosInstance } from '@/rest'

/**
 * Creates an API interface for interacting with group trade API v2.
 */
const createGroupTradeApiV2 = () => {
  return {
    /**
     * Get group uploads from v2 API
     * @param {string} filter - optional filter query string
     * @param {boolean} includePlacedFilter - whether to include is_placed=true filter (default: true)
     * @param {string} dateFrom - optional date_from parameter for filtering (YYYY-MM-DD format)
     * @param {{orderBy?: string, sortDirection?: string}} sort - optional server-side
     *   sorting. orderBy is one of created_at | updated_at | name (API default
     *   created_at), sortDirection one of asc | desc (API default desc).
     * @param {Array<string>} statuses - optional group upload statuses to filter
     *   by. Sent as a repeated `status` query param; empty/null means no filter.
     * @returns {Promise<Array<{
     *   id: string,
     *   name: string,
     *   createdAt: string,
     *   owner: string,
     *   isPlaced: boolean,
     *   updatedAt: string
     * }>>}
     */
    getGroupUploads: (
      filter,
      includePlacedFilter = true,
      dateFrom = null,
      sort = null,
      statuses = null
    ) => {
      let url = '/v2/group_upload/group_uploads'
      const queryParams = []

      if (includePlacedFilter) {
        queryParams.push('is_placed=true')
      }

      if (filter) {
        queryParams.push(filter)
      }

      if (dateFrom) {
        queryParams.push(`date_from=${dateFrom}`)
      }

      if (sort?.orderBy) {
        queryParams.push(`order_by=${sort.orderBy}`)
      }

      if (sort?.sortDirection) {
        queryParams.push(`sort_direction=${sort.sortDirection}`)
      }

      if (Array.isArray(statuses) && statuses.length > 0) {
        for (const status of statuses) {
          queryParams.push(`status=${encodeURIComponent(status)}`)
        }
      }

      if (queryParams.length > 0) {
        url += `?${queryParams.join('&')}`
      }

      return axiosInstance.get(url)
    },

    /**
     * Create and upload CSV file for group trade v2
     * @param {FormData} formData - FormData containing file and metadata
     * @returns {Promise<{
     *   group_upload_id: string,
     *   total_records: number,
     *   successful_inserts: number,
     *   failed_inserts: number
     * }>}
     */
    createAndUpload: (formData) =>
      axiosInstance.post('/v2/group_upload/upload', formData),

    /**
     * Create order for a specific group upload v2
     * @param {string} groupUploadId - The group upload ID
     * @param {object} payload - Order payload data
     * @returns {Promise<{
     *   total_group_orders: number,
     *   successful_orders: number,
     *   failed_orders: number
     * }>}
     */
    createOrder: (groupUploadId, payload) =>
      axiosInstance.post(`/v2/group_upload/${groupUploadId}/order`, payload, {
        headers: { 'X-Idempotency-Key': groupUploadId },
        // No retries: this places real orders, and the failure (503, 500,
        // network) should reach the user immediately rather than after three
        // exponential-backoff attempts.
        'axios-retry': { retries: 0 },
      }),

    /**
     * Get a specific group upload by ID.
     * `user_orders[].execution_reports` and `block_trade_price_history` are
     * only available here — /orders/{id}/details returns an empty
     * `price_history` for group trades.
     * @param {string} groupUploadId - The group upload ID
     * @returns {Promise<{
     *   group_upload: {
     *     id: string,
     *     name: string,
     *     description: string,
     *     owner: string,
     *     status: string,
     *     is_placed: boolean,
     *     created_at: string,
     *     updated_at: string,
     *     orders: Array
     *   },
     *   user_orders: Array,
     *   block_trade_price_history: Array<{
     *     datetime: string,
     *     order_type: string,
     *     limit_price: string | null,
     *     stop_price: string | null,
     *     price: string,
     *     status: string
     *   }>
     * }>}
     */
    getGroupUpload: (groupUploadId) =>
      axiosInstance.get(`/v2/group_upload/group_uploads/${groupUploadId}`),

    /**
     * Cancel a group upload by ID
     * @param {string} groupUploadId - The group upload ID
     * @returns {Promise<{detail: string}>}
     */
    cancel: (groupUploadId) =>
      axiosInstance.post(`/v2/group_upload/${groupUploadId}/cancel`),

    /**
     * Modify a group upload by ID
     * @param {string} groupUploadId - The group upload ID
     * @param {number} limitPrice - The new limit price
     * @returns {Promise<{detail: string}>}
     */
    modify: (groupUploadId, limitPrice) =>
      axiosInstance.post(
        `/v2/group_upload/${groupUploadId}/modify?new_limit_price=${limitPrice}`
      ),

    /**
     * Delete a group upload by ID
     * @param {string} groupUploadId - The group upload ID
     * @returns {Promise<{detail: string}>}
     */
    deleteGroupUpload: (groupUploadId) =>
      axiosInstance.delete(`/v2/group_upload/group_uploads/${groupUploadId}`),

    /**
     * Get orders for a specific group upload with pagination
     * @param {string} groupUploadId - The group upload ID
     * @param {number} offset - Offset for pagination (default: 0)
     * @param {number} limit - Limit for pagination (default: 50)
     * @returns {Promise<{items: Array}>}
     */
    getGroupUploadOrders: (groupUploadId, offset = 0, limit = 50) =>
      axiosInstance.get(
        `/v2/group_upload/group_uploads/${groupUploadId}/orders?offset=${offset}&limit=${limit}`
      ),
  }
}

const groupTradeApiV2 = createGroupTradeApiV2()

export { groupTradeApiV2 }
